import React from 'react';

// Static figure: one GPT-5.5 inference step vs one Firecracker wake, to scale.
// The whole point is the visual gap — the wake is a sliver near zero while the
// think step runs far to the right, so every wake finishes inside the thought.
const AXIS_MAX = 4000; // ms shown; think max (6381) noted in text, off-scale
const X0 = 150;        // left edge of plot (label gutter)
const X1 = 780;        // right edge
const W = 820;
const H = 230;

const xf = (ms: number) => X0 + (Math.min(ms, AXIS_MAX) / AXIS_MAX) * (X1 - X0);

const THINK = { p50: 1631, p95: 3686, max: 6381 };
const WAKE = { p50: 152, p95: 187 };

const ThinkWakeFigure: React.FC = () => {
  const thinkY = 66;
  const wakeY = 128;
  const barH = 30;
  const axisY = 186;
  const ticks = [0, 1000, 2000, 3000, 4000];

  return (
    <figure className="my-8 reading-figure" style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}>
      <div style={{ border: '1px solid #e7e5e4', borderRadius: 12, background: '#fafaf9', padding: '16px 8px 10px' }}>
        <div style={{ padding: '0 12px', fontSize: 14, fontWeight: 600, color: '#1c1917' }}>
          One inference step vs one wake, to scale
        </div>
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
          {/* axis */}
          <line x1={X0} y1={axisY} x2={X1} y2={axisY} stroke="#d6d3d1" strokeWidth={1} />
          {ticks.map((ms) => (
            <g key={ms}>
              <line x1={xf(ms)} y1={axisY} x2={xf(ms)} y2={axisY + 5} stroke="#d6d3d1" strokeWidth={1} />
              <text x={xf(ms)} y={axisY + 18} textAnchor="middle" style={{ fontSize: 11, fill: '#a8a29e' }}>
                {ms === 0 ? '0' : `${ms / 1000}s`}
              </text>
            </g>
          ))}

          {/* think bar: solid to p50, lighter p50->p95 */}
          <text x={X0 - 12} y={thinkY + barH / 2 + 4} textAnchor="end" style={{ fontSize: 13, fill: '#57534e' }}>GPT-5.5 think</text>
          <rect x={xf(0)} y={thinkY} width={xf(THINK.p50) - xf(0)} height={barH} rx={4} fill="#fb923c" />
          <rect x={xf(THINK.p50)} y={thinkY} width={xf(THINK.p95) - xf(THINK.p50)} height={barH} fill="#fdba74" />
          <text x={xf(THINK.p95) + 10} y={thinkY + barH / 2 + 4} style={{ fontSize: 12, fill: '#9a3412', fontWeight: 600 }}>
            1631 / 3686 ms
          </text>

          {/* wake bar: a sliver near zero */}
          <text x={X0 - 12} y={wakeY + barH / 2 + 4} textAnchor="end" style={{ fontSize: 13, fill: '#57534e' }}>Firecracker wake</text>
          <rect x={xf(0)} y={wakeY} width={Math.max(3, xf(WAKE.p95) - xf(0))} height={barH} rx={3} fill="#3b82f6" />
          <text x={xf(WAKE.p95) + 10} y={wakeY + barH / 2 + 4} style={{ fontSize: 12, fill: '#1e40af', fontWeight: 600 }}>
            152 / 187 ms
          </text>

          {/* "wakes done" marker */}
          <line x1={xf(WAKE.p95)} y1={wakeY - 12} x2={xf(WAKE.p95)} y2={axisY} stroke="#3b82f6" strokeWidth={1} strokeDasharray="4 4" opacity={0.7} />
          <text x={xf(WAKE.p95) + 6} y={wakeY - 16} style={{ fontSize: 10.5, fill: '#3b82f6' }}>every wake done here</text>
        </svg>
      </div>
      <figcaption className="reading-figure__caption">
        The speed difference between GPT-5.5 model thinking and Firecracker restore-to-ready.
      </figcaption>
    </figure>
  );
};

export default ThinkWakeFigure;
